<template>
  <div class="fee-list">
    <div class="fee-rule">
      <div class="title">
        {{ $t('EvCharging.FeeRuleTitle') }}
      </div>
      <ul>
        <li>{{ $t('EvCharging.FeeRule1') }}</li>
        <li>{{ $t('EvCharging.FeeRule2') }}</li>
        <li>{{ $t('EvCharging.FeeRule3') }}</li>
        <li>{{ $t('EvCharging.FeeRule4') }}</li>
      </ul>
    </div>
    <!-- Filter -->
    <FilterFrame ref="filter" :filter-form="paymentFilter" :auto-search="true" class="filter-frame" @search="search" @clear="clearSearch()" />
    <div class="function">
      <div class="right">
        <span class="result">{{ $t('EvCharging.TotalCount', { count: totalCount }) }}</span>
      </div>
    </div>
    <!-- Table -->
    <el-table
      :data="chargingFeeData"
      style="width: 100%"
    >
      <el-table-column
        v-for="[key, value] of Object.entries(dynamicChargingFeeStructure) "
        :key="key" :prop="key" :label="value.label" :min-width="value.minWidth"
      >
        <template #header>
          <div v-if="key === 'ServiceChargeAmount'" class="header-tooltip">
            <el-tooltip class="item" effect="dark" :content="$t('EvCharging.ServiceChargeTooltip', {ServiceChargeRatePercentage: chargingFeature.ServiceChargeRatePercentage})" placement="top">
              <span>{{ value.label }} <i
                class="icon-filled-info"
                aria-hidden="true"
              /></span>
            </el-tooltip>
          </div>
          <div v-else-if="key === 'SystemUsageFeeAmount'" class="header-tooltip">
            <el-tooltip class="item" effect="dark" :content="$t('EvCharging.SystemUsageFeeTooltip', {SystemUsageFeeRate : chargingFeature.SystemUsageFeeRate})" placement="top">
              <span>{{ value.label }} <i
                class="icon-filled-info"
                aria-hidden="true"
              /></span>
            </el-tooltip>
          </div>
          <div v-else>
            {{ value.label }}
          </div>
        </template>
        <template slot-scope="scope">
          <div v-if="key === 'Period'" class="regular">
            <pre>{{ scope.row.Period.replace('-', $t('EvCharging.Year')) }}{{ $t('EvCharging.Month') }}</pre>
          </div>
          <div v-else-if="key === 'Status'" class="regular">
            <pre :class="{ 'not-approved': scope.row.Status === '未核定' }">{{ scope.row.Status }}</pre>
          </div>
          <div v-else-if="key === 'InvoiceDate'" class="regular">
            <pre>{{ scope.row.Invoice?.IssuedDate ? $newFormatDate(scope.row.Invoice.IssuedDate, 'yyyy/MM/dd') : '' }}</pre>
          </div>
          <div v-else-if="key === 'InvoiceSn'" class="regular">
            <pre>{{ scope.row.Invoice?.SerialNumber || '' }}</pre>
          </div>
          <div v-else-if="key === 'SystemUsageFeeAmount' && chargingFeature.SystemUsageFeeByPowerUsage" class="regular">
            <pre>{{ scope.row[key]|| 0 }}</pre>
          </div>
          <div v-else class="regular">
            <pre>{{ scope.row[key] }}</pre>
          </div>
        </template>
      </el-table-column>
      <el-table-column :label="$t('EvCharging.Action')" min-width="120">
        <template slot-scope="scope">
          <div class="operate">
            <el-button size="mini" @click="downloadFee(scope.row.Period)">
              {{ $t('EvCharging.DownloadFee') }}
            </el-button>
          </div>
        </template>
      </el-table-column>
    </el-table>
    <TableLoading :is-loading="isLoading" />
    <el-pagination
      background
      layout="total, prev, pager, next, jumper"
      :total="totalCount"
      @current-change="getList"
    >
    </el-pagination>
  </div>
</template>

<script>
import TableLoading from '@/components/TableLoading'
import FilterFrame from '@/components/FilterFrame'
import { apiEvCharging } from '@/api/EvCharging'

export default {
  components: {
    FilterFrame,
    TableLoading
  },
  props: {
    chargingFeature: {
      type: Object,
      required: true
    }
  },
  data () {
    const maxCount = 10
    const defaultReq = {
      'Paging.Offset': 0,
      'Paging.MaxCount': 10
    }
    const req = { ...defaultReq }
    const isLoading = false
    return {
      chargingFeeStructure: {
        Period: {
          label: this.$t('EvCharging.Period'),
          minWidth: 160
        },
        CollectedAmount: {
          label: this.$t('EvCharging.CollectedAmount'),
          minWidth: 140
        },
        ServiceChargeAmount: {
          label: this.$t('EvCharging.ServiceChargeAmount'),
          minWidth: 140
        },
        SystemUsageFeeAmount: {
          label: this.$t('EvCharging.SystemUsageFeeAmount'),
          minWidth: 140
        },
        DisbursementAmount: {
          label: this.$t('EvCharging.DisbursementAmount'),
          minWidth: 140
        },
        Status: {
          label: this.$t('EvCharging.Status'),
          minWidth: 140
        },
        InvoiceDate: {
          label: this.$t('EvCharging.InvoiceDate'),
          minWidth: 200
        },
        InvoiceSn: {
          label: this.$t('EvCharging.InvoiceSn'),
          minWidth: 140
        }
      },
      chargingFeeData: [],
      totalCount: 0,
      defaultReq,
      req,
      isLoading,
      maxCount,
      paymentFilter: [
        {
          label: this.$t('EvCharging.Period'),
          name: 'Period',
          type: 'input',
          value: ''
        },
        {
          label: this.$t('EvCharging.Status'),
          name: 'Status',
          type: 'select',
          value: '',
          options: [
            {
              label: this.$t('EvCharging.NotApproved'),
              value: '未核定'
            },
            {
              label: this.$t('EvCharging.Approved'),
              value: '已核定'
            },
            {
              label: this.$t('EvCharging.Invoiced'),
              value: '已開發票'
            }
          ]
        },
        {
          label: this.$t('EvCharging.InvoiceDate'),
          name: 'InvoiceIssueDateRange',
          type: 'date'
        },
        {
          label: this.$t('EvCharging.InvoiceSn'),
          name: 'InvoiceSn',
          type: 'input',
          value: ''
        }
      ]
    }
  },
  computed: {
    dynamicChargingFeeStructure () {
      // 動態添加 SystemUsageFeeAmount
      const structure = { ...this.chargingFeeStructure }
      if (!this.chargingFeature.SystemUsageFeeByPowerUsage) {
        delete structure.SystemUsageFeeAmount
      }
      return structure
    }
  },
  created () {
    this.checkHaveNewFee()
  },
  methods: {
    checkHaveNewFee () {
      const req = {
        'Paging.Offset': 0,
        'Paging.MaxCount': 10,
        Status: '未核定'
      }
      apiEvCharging.ChargingFeeList.getChargingFeeList(req).then(res => {
        if (res?.data?.Data?.Items) {
          const today = new Date()
          const nowMonthPeriod = `${today.getFullYear()}-${(today.getMonth() + 1).toString().padStart(2, '0')}`
          const nowMonthDate = res.data.Data.Items.find((item) => item.Period === nowMonthPeriod && (!item.DownloadCount || item.DownloadCount <= 0))
          if (nowMonthDate) {
            this.$alert(this.$t('EvCharging.HaveNewFeeAlertBody'), this.$t('EvCharging.HaveNewFeeAlertTitle'), {
              confirmButtonText: this.$t('General.OK'),
              type: 'warning',
              callback: action => {}
            })
          }
        }
      }).catch(err => {
        console.log(err)
      })
    },
    getList (page = 1) {
      // 清空列表且呈現 loading 畫面
      this.inputLogList = []
      this.isLoading = true
      this.req['Paging.Offset'] = (page - 1) * 10
      apiEvCharging.ChargingFeeList.getChargingFeeList(this.req).then(res => {
        if (res?.data?.Data) {
          this.totalCount = res.data.Data.TotalCount
          this.chargingFeeData = res.data.Data.Items
        }
      }).catch(err => {
        console.log(err)
      }).finally(() => {
        this.isLoading = false
      })
    },
    search (filterOptions) {
      const copyFilterOptions = _cloneDeep(filterOptions)
      if (copyFilterOptions?.InvoiceIssueDateRange) {
        copyFilterOptions['InvoiceIssueDateRange.From'] = copyFilterOptions.InvoiceIssueDateRange[0]
        copyFilterOptions['InvoiceIssueDateRange.To'] = copyFilterOptions.InvoiceIssueDateRange[1]
      } else {
        delete copyFilterOptions['InvoiceIssueDateRange.From']
        delete copyFilterOptions['InvoiceIssueDateRange.To']
      }
      delete copyFilterOptions.InvoiceIssueDateRange
      if (copyFilterOptions.Period) {
        const regex = new RegExp(/^\d{4}年\d{2}月$/)
        if (regex.test(copyFilterOptions.Period)) {
          const numbers = copyFilterOptions.Period.match(/\d+/g)
          const year = numbers[0]
          const month = numbers[1]
          copyFilterOptions.Period = `${year}-${month}`
        }
      }
      this.req = { ...copyFilterOptions, 'Paging.MaxCount': this.maxCount }
      this.getList()
    },
    downloadFee (period) {
      const config = {
        headers: {
          'content-type': 'application/x-www-form-urlencoded'
        },
        responseType: 'blob' // important
      }
      apiEvCharging.ChargingFeeList.getChargingFeeListExcel(period, config).then(res => {
        this.$downloadFile(res.data, `${decodeURIComponent(res.headers['content-disposition'].split(';')[2].replace(/"/g, '').replace("filename*=UTF-8''", '')).trim()}`)
      }).catch(err => {
        console.log(err)
      })
    },
    clearSearch () {
      this.req = { ...this.defaultReq }
      this.getList()
    }
  }
}
</script>

<style lang="scss" scoped>
.fee-list {
  .fee-rule {
    text-align: left;
    margin-block: 24px;
    .title {
      font-size: map-get($fontSize, large);
      font-weight: bold;
      color: $mainFontColor;
    }
    ul {
      margin-top: 8px;
      padding-inline-start: 20px;
      li {
        list-style: disc;
        line-height: 1.5pc;
      }
    }
  }
  .function {
    width: 100%;
    display: flex;
    justify-content: right;
    align-items: center;
    padding-block: 32px;
    .right {
      .result {
        margin-left: 12px;
        font-size: map-get($fontSize, medium);
        color: $mainFontColor;
      }
    }
  }
  .not-approved {
    color: $dangerColor;
  }
  ::v-deep .el-table {
    @include basicTable(false);
    margin-bottom: 17px;
  }
  ::v-deep .el-checkbox.is-disabled {
    display: none;
  }
  .el-pagination {
    display: flex;
    justify-content: flex-start;
    margin-top: 28px;
  }
}
</style>
